import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Settings, Clock, Calendar, AlertTriangle, Users, Save, RefreshCw, Zap, GitBranch, Edit2, Repeat, History, CheckCircle2, XCircle, Timer } from 'lucide-react';
import { toast } from 'sonner';
import { usePermissions } from '../context/PermissionsContext';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGES = [
  { value: 'cold_data', label: 'Cold Data' },
  { value: 'attempting_contact', label: 'Attempting Contact' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'consultation_scheduled', label: 'Consultation Scheduled' },
  { value: 'consultation_completed', label: 'Consultation Completed' },
  { value: 'warm_follow_up', label: 'Warm Follow-Up' },
  { value: 'rsvp_office_event', label: 'RSVP Office Event' },
  { value: 'attended_office_event', label: 'Attended Office Event' },
  { value: 'closed_won', label: 'Closed Won' },
  { value: 'closed_lost', label: 'Closed Lost' },
  { value: 'long_term_nurture', label: 'Long-Term Nurture' },
];

const ASSIGNEE_RULES = [
  { value: 'lead_owner', label: 'Lead Owner' },
  { value: 'sales_manager_role', label: 'Sales Manager' },
  { value: 'finance_role', label: 'Finance' },
  { value: 'admin_role', label: 'Admin' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
];

const DUPLICATE_RULES = [
  { value: 'skip_if_open_same_type', label: 'Skip if open task of same type' },
  { value: 'skip_if_open_same_template', label: 'Skip if open task from same template' },
  { value: 'allow_duplicates', label: 'Allow duplicates' },
];

const JOB_TYPE_LABELS = {
  'all': 'All Jobs',
  'recurring_followup': 'Recurring Follow-Up',
  'sla_escalation': 'SLA Escalation',
  'sales_week': 'Sales Week'
};

export default function AutomationSettingsPage() {
  const { role } = usePermissions();
  const [settings, setSettings] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [runningJobs, setRunningJobs] = useState(false);
  const [seedingTemplates, setSeedingTemplates] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [jobHistory, setJobHistory] = useState([]);
  const [salesWeekStatus, setSalesWeekStatus] = useState(null);

  const canEdit = ['admin', 'owner', 'sales_manager'].includes(role);
  const canViewHistory = ['admin', 'owner'].includes(role);



  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    let hasError = false;
    let errorMessage = '';
    
    try {
      // Fetch core data (settings and templates) first
      const [settingsRes, templatesRes] = await Promise.all([
        fetch(`${API_URL}/api/automation/settings`, { credentials: 'include' }),
        fetch(`${API_URL}/api/automation/templates`, { credentials: 'include' })
      ]);
      
      if (settingsRes.ok) {
        setSettings(await settingsRes.json());
      } else {
        hasError = true;
        errorMessage = 'Settings unavailable';
      }
      
      if (templatesRes.ok) {
        setTemplates(await templatesRes.json());
      } else {
        hasError = true;
        errorMessage = errorMessage ? `${errorMessage}, Templates unavailable` : 'Templates unavailable';
      }
      
      // Fetch optional data - don't fail on these
      try {
        const [schedulerRes, salesWeekRes] = await Promise.all([
          fetch(`${API_URL}/api/automation/scheduler-status`, { credentials: 'include' }),
          fetch(`${API_URL}/api/automation/sales-week-status`, { credentials: 'include' })
        ]);
        
        if (schedulerRes.ok) {
          setSchedulerStatus(await schedulerRes.json());
        }
        if (salesWeekRes.ok) {
          setSalesWeekStatus(await salesWeekRes.json());
        }
      } catch (optionalErr) {
        logger.warn('Optional automation data fetch failed:', optionalErr);
      }
      
      // Only fetch job history for admin/owner
      if (['admin', 'owner'].includes(role)) {
        try {
          const historyRes = await fetch(`${API_URL}/api/automation/job-history?limit=10`, { credentials: 'include' });
          if (historyRes.ok) {
            setJobHistory(await historyRes.json());
          }
        } catch (historyErr) {
          logger.warn('Job history fetch failed:', historyErr);
        }
      }
      
      if (hasError) {
        toast.error(`Failed to load some automation data: ${errorMessage}`);
      }
    } catch (err) {
      logger.error('Automation fetch error:', err);
      toast.error('Failed to load automation data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!canEdit) return;
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/automation/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(settings)
      });
      if (response.ok) {
        setSettings(await response.json());
        toast.success('Settings saved successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save settings');
      }
    } catch (err) {
      toast.error('Network error saving settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!editTemplate) return;
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/automation/templates/${editTemplate.template_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: editTemplate.name,
          task_type: editTemplate.task_type,
          default_title: editTemplate.default_title,
          default_description: editTemplate.default_description,
          default_priority: editTemplate.default_priority,
          due_rule: editTemplate.due_rule,
          assignee_rule: editTemplate.assignee_rule,
          duplicate_prevention_rule: editTemplate.duplicate_prevention_rule,
          enabled: editTemplate.enabled
        })
      });
      if (response.ok) {
        toast.success('Template saved');
        setEditDialogOpen(false);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save template');
      }
    } catch (err) {
      toast.error('Network error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleTemplate = async (template) => {
    if (!canEdit) return;
    try {
      const response = await fetch(`${API_URL}/api/automation/templates/${template.template_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ enabled: !template.enabled })
      });
      if (response.ok) {
        setTemplates(prev => prev.map(t => 
          t.template_id === template.template_id 
            ? { ...t, enabled: !t.enabled }
            : t
        ));
        toast.success(template.enabled ? 'Template disabled' : 'Template enabled');
      }
    } catch (err) {
      toast.error('Failed to update template');
    }
  };

  const handleRunJobs = async () => {
    setRunningJobs(true);
    try {
      const response = await fetch(`${API_URL}/api/automation/run-recurring-jobs`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        const result = await response.json();
        toast.success(`Jobs executed: ${result.tasks_created || 0} tasks created`);
        // Refresh job history
        if (canViewHistory) {
          const historyRes = await fetch(`${API_URL}/api/automation/job-history?limit=10`, { credentials: 'include' });
          if (historyRes.ok) {
            setJobHistory(await historyRes.json());
          }
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to run jobs');
      }
    } catch (err) {
      toast.error('Network error');
    } finally {
      setRunningJobs(false);
    }
  };

  const handleSeedTemplates = async () => {
    if (!window.confirm('This will reset all automation templates to defaults. Custom templates will be deleted. Continue?')) {
      return;
    }
    setSeedingTemplates(true);
    try {
      const response = await fetch(`${API_URL}/api/automation/templates/seed-defaults`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || 'Templates seeded successfully');
        // Refresh templates
        const templatesRes = await fetch(`${API_URL}/api/automation/templates`, { credentials: 'include' });
        if (templatesRes.ok) {
          setTemplates(await templatesRes.json());
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to seed templates');
      }
    } catch (err) {
      toast.error('Network error');
    } finally {
      setSeedingTemplates(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const toggleSalesWeekStage = (stage) => {
    const currentStages = settings?.sales_week_stages || [];
    const newStages = currentStages.includes(stage)
      ? currentStages.filter(s => s !== stage)
      : [...currentStages, stage];
    updateSetting('sales_week_stages', newStages);
  };

  const templatesByStage = useMemo(() => {
    const grouped = {};
    templates.forEach(t => {
      if (t.scope === 'stage' && t.stage) {
        if (!grouped[t.stage]) grouped[t.stage] = [];
        grouped[t.stage].push(t);
      }
    });
    return grouped;
  }, [templates]);

  const salesWeekStages = useMemo(
    () => STAGES.filter(s => !['closed_won', 'closed_lost'].includes(s.value)),
    []
  );

  const openEditDialog = (template) => {
    setEditTemplate({ ...template });
    setEditDialogOpen(true);
  };

  const updateEditTemplate = (key, value) => {
    setEditTemplate(prev => ({ ...prev, [key]: value }));
  };

  const updateDueRule = (key, value) => {
    setEditTemplate(prev => ({
      ...prev,
      due_rule: { ...prev.due_rule, [key]: value }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="automation-settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Zap className="w-6 h-6 text-gold" />
            Automation Console
          </h1>
          <p className="text-slate-500 mt-1">Configure task automation rules, templates, and schedules</p>
        </div>
        <div className="flex gap-3 items-center">
          {/* Scheduler Status Indicator */}
          {schedulerStatus && (
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
              schedulerStatus.scheduler_running 
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                : 'bg-red-50 text-red-700 border border-red-200'
            }`} data-testid="scheduler-status">
              <span className={`w-2 h-2 rounded-full ${
                schedulerStatus.scheduler_running ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'
              }`}></span>
              {schedulerStatus.scheduler_running ? 'Scheduler Active' : 'Scheduler Stopped'}
            </div>
          )}
          {(role === 'admin' || role === 'owner') && (
            <Button
              variant="outline"
              onClick={handleRunJobs}
              disabled={runningJobs}
              data-testid="run-jobs-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${runningJobs ? 'animate-spin' : ''}`} />
              Run Jobs Now
            </Button>
          )}
          {/* Show Seed button when templates are empty or for manual reset */}
          {(role === 'admin' || role === 'owner') && (
            <Button
              variant={templates.length === 0 ? "default" : "outline"}
              onClick={handleSeedTemplates}
              disabled={seedingTemplates}
              className={templates.length === 0 ? "bg-amber-500 hover:bg-amber-600 text-white" : ""}
              data-testid="seed-templates-btn"
            >
              <Zap className={`w-4 h-4 mr-2 ${seedingTemplates ? 'animate-pulse' : ''}`} />
              {seedingTemplates ? 'Seeding...' : templates.length === 0 ? 'Load Default Templates' : 'Reset Templates'}
            </Button>
          )}
          {canEdit && (
            <Button
              onClick={handleSaveSettings}
              disabled={saving}
              className="bg-gold hover:bg-gold/90 text-slate-900"
              data-testid="save-settings-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save All'}
            </Button>
          )}
        </div>
      </div>

      {/* Scheduler Info Card */}
      {schedulerStatus?.scheduler_running && schedulerStatus.jobs?.length > 0 && (
        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardContent className="py-3 px-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-medium text-emerald-800">
                  Hourly automation jobs running automatically
                </span>
              </div>
              <span className="text-xs text-emerald-600">
                Next run: {schedulerStatus.jobs[0]?.next_run_time 
                  ? new Date(schedulerStatus.jobs[0].next_run_time).toLocaleString()
                  : 'N/A'}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Warning when no templates */}
      {templates.length === 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4 px-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <div>
                  <span className="text-sm font-medium text-amber-800">
                    No automation templates configured
                  </span>
                  <p className="text-xs text-amber-600 mt-0.5">
                    Stage-based task automation won't work until templates are loaded.
                  </p>
                </div>
              </div>
              {(role === 'admin' || role === 'owner') && (
                <Button 
                  size="sm"
                  onClick={handleSeedTemplates}
                  disabled={seedingTemplates}
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                >
                  <Zap className="w-4 h-4 mr-1" />
                  Load Defaults
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Job History Panel - Admin/Owner only */}
      {canViewHistory && jobHistory.length > 0 && (
        <Card data-testid="job-history-panel">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <History className="w-4 h-4 text-slate-600" />
              Job Run History
            </CardTitle>
            <CardDescription>Last 10 automation job runs</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-slate-500">
                    <th className="pb-2 pr-4 font-medium">Status</th>
                    <th className="pb-2 pr-4 font-medium">Type</th>
                    <th className="pb-2 pr-4 font-medium">Timestamp</th>
                    <th className="pb-2 pr-4 font-medium">Duration</th>
                    <th className="pb-2 pr-4 font-medium">Tasks</th>
                    <th className="pb-2 font-medium">Errors</th>
                  </tr>
                </thead>
                <tbody>
                  {jobHistory.map((job) => (
                    <tr key={job.log_id} className="border-b last:border-0">
                      <td className="py-2 pr-4">
                        {job.status === 'success' ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        ) : job.status === 'error' ? (
                          <XCircle className="w-4 h-4 text-red-500" />
                        ) : (
                          <Timer className="w-4 h-4 text-amber-500 animate-pulse" />
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        <Badge variant="outline" className="text-xs">
                          {JOB_TYPE_LABELS[job.job_type] || job.job_type}
                        </Badge>
                      </td>
                      <td className="py-2 pr-4 text-slate-600">
                        {job.started_at ? new Date(job.started_at).toLocaleString() : '-'}
                      </td>
                      <td className="py-2 pr-4 text-slate-600">
                        {job.duration_ms != null ? `${job.duration_ms}ms` : '-'}
                      </td>
                      <td className="py-2 pr-4">
                        <span className={`font-medium ${job.tasks_created > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                          {job.tasks_created}
                        </span>
                        <span className="text-slate-400 text-xs ml-1">
                          ({job.leads_processed} leads)
                        </span>
                      </td>
                      <td className="py-2">
                        {job.errors && job.errors.length > 0 ? (
                          <span className="text-red-600 text-xs" title={job.errors.join('\n')}>
                            {job.errors.length} error(s)
                          </span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {!canEdit && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <p className="text-amber-800">You have read-only access to automation settings.</p>
        </div>
      )}

      <Tabs defaultValue="stage" className="w-full">
        <TabsList className="grid w-full grid-cols-4 mb-6">
          <TabsTrigger value="stage" data-testid="tab-stage">
            <GitBranch className="w-4 h-4 mr-2" />
            Stage Tasks
          </TabsTrigger>
          <TabsTrigger value="recurring" data-testid="tab-recurring">
            <Repeat className="w-4 h-4 mr-2" />
            Recurring
          </TabsTrigger>
          <TabsTrigger value="sla" data-testid="tab-sla">
            <AlertTriangle className="w-4 h-4 mr-2" />
            SLA Escalation
          </TabsTrigger>
          <TabsTrigger value="salesweek" data-testid="tab-salesweek">
            <Calendar className="w-4 h-4 mr-2" />
            Sales Week
          </TabsTrigger>
        </TabsList>

        {/* Stage Tasks Tab */}
        <TabsContent value="stage" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Stage-Based Task Templates</CardTitle>
              <CardDescription>
                Tasks are automatically created when leads enter these stages
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {STAGES.map(stage => {
                const stageTemplates = templatesByStage[stage.value] || [];
                if (stageTemplates.length === 0) return null;
                
                return (
                  <div key={stage.value} className="border-b pb-4 last:border-0">
                    <h3 className="font-medium text-slate-900 mb-3">{stage.label}</h3>
                    <div className="space-y-2">
                      {stageTemplates.map(template => (
                        <div 
                          key={template.template_id}
                          className={`flex items-center justify-between p-3 rounded-lg border ${
                            template.enabled ? 'bg-white' : 'bg-slate-50 opacity-60'
                          }`}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{template.default_title}</span>
                              <Badge variant="outline" className="text-xs">
                                {template.default_priority}
                              </Badge>
                            </div>
                            <p className="text-xs text-slate-500 mt-1">
                              Due: {template.due_rule?.type === 'relative' 
                                ? `${template.due_rule.days}d ${template.due_rule.hours}h`
                                : `Event time ${template.due_rule?.offset_hours || 0}h`}
                              {' • '}Assign to: {template.assignee_rule.replace('_', ' ')}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={template.enabled}
                              onCheckedChange={() => handleToggleTemplate(template)}
                              disabled={!canEdit}
                            />
                            {canEdit && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditDialog(template)}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Recurring Tasks Tab */}
        <TabsContent value="recurring" className="space-y-4">
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Warm Follow-Up */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-gold" />
                  Warm Follow-Up
                </CardTitle>
                <CardDescription>
                  Recurring tasks for leads in Warm Follow-Up stage
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Enabled</Label>
                  <Switch
                    checked={settings?.warm_followup_enabled || false}
                    onCheckedChange={(v) => updateSetting('warm_followup_enabled', v)}
                    disabled={!canEdit}
                    data-testid="warm-enabled"
                  />
                </div>
                <div>
                  <Label>Interval (days)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="30"
                    value={settings?.warm_followup_interval_days || 7}
                    onChange={(e) => updateSetting('warm_followup_interval_days', parseInt(e.target.value) || 7)}
                    disabled={!canEdit}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Task Type</Label>
                  <Input
                    value={settings?.warm_followup_task_type || ''}
                    onChange={(e) => updateSetting('warm_followup_task_type', e.target.value)}
                    disabled={!canEdit}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select
                    value={settings?.warm_followup_priority || 'normal'}
                    onValueChange={(v) => updateSetting('warm_followup_priority', v)}
                    disabled={!canEdit}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map(p => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Assign To</Label>
                  <Select
                    value={settings?.warm_followup_assignee_rule || 'lead_owner'}
                    onValueChange={(v) => updateSetting('warm_followup_assignee_rule', v)}
                    disabled={!canEdit}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ASSIGNEE_RULES.map(r => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Long-Term Nurture */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-gold" />
                  Long-Term Nurture
                </CardTitle>
                <CardDescription>
                  Recurring tasks for leads in Long-Term Nurture stage
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Enabled</Label>
                  <Switch
                    checked={settings?.long_term_nurture_enabled || false}
                    onCheckedChange={(v) => updateSetting('long_term_nurture_enabled', v)}
                    disabled={!canEdit}
                    data-testid="nurture-enabled"
                  />
                </div>
                <div>
                  <Label>Interval (days)</Label>
                  <Input
                    type="number"
                    min="7"
                    max="90"
                    value={settings?.long_term_nurture_interval_days || 30}
                    onChange={(e) => updateSetting('long_term_nurture_interval_days', parseInt(e.target.value) || 30)}
                    disabled={!canEdit}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Task Type</Label>
                  <Input
                    value={settings?.long_term_nurture_task_type || ''}
                    onChange={(e) => updateSetting('long_term_nurture_task_type', e.target.value)}
                    disabled={!canEdit}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select
                    value={settings?.long_term_nurture_priority || 'low'}
                    onValueChange={(v) => updateSetting('long_term_nurture_priority', v)}
                    disabled={!canEdit}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map(p => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Assign To</Label>
                  <Select
                    value={settings?.long_term_nurture_assignee_rule || 'lead_owner'}
                    onValueChange={(v) => updateSetting('long_term_nurture_assignee_rule', v)}
                    disabled={!canEdit}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ASSIGNEE_RULES.map(r => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* SLA Escalation Tab */}
        <TabsContent value="sla" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-gold" />
                SLA Escalation Settings
              </CardTitle>
              <CardDescription>
                Configure task overdue thresholds and escalation rules
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>SLA Escalation Enabled</Label>
                  <p className="text-sm text-slate-500">Automatically escalate overdue tasks</p>
                </div>
                <Switch
                  checked={settings?.sla_enabled || false}
                  onCheckedChange={(v) => updateSetting('sla_enabled', v)}
                  disabled={!canEdit}
                  data-testid="sla-enabled"
                />
              </div>
              
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label>Overdue Threshold (hours)</Label>
                  <p className="text-xs text-slate-500 mb-2">
                    Escalate tasks overdue by more than this
                  </p>
                  <Input
                    type="number"
                    min="1"
                    max="168"
                    value={settings?.sla_overdue_hours || 24}
                    onChange={(e) => updateSetting('sla_overdue_hours', parseInt(e.target.value) || 24)}
                    disabled={!canEdit}
                    data-testid="sla-hours"
                  />
                </div>
                
                <div>
                  <Label>Escalation Role</Label>
                  <p className="text-xs text-slate-500 mb-2">
                    Assign escalation tasks to this role
                  </p>
                  <Select
                    value={settings?.sla_escalation_role || 'sales_manager'}
                    onValueChange={(v) => updateSetting('sla_escalation_role', v)}
                    disabled={!canEdit}
                  >
                    <SelectTrigger data-testid="sla-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sales_manager">Sales Manager</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="owner">Owner</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>SLA Breach Tag Name</Label>
                  <p className="text-xs text-slate-500 mb-2">
                    Tag applied to leads with SLA breaches
                  </p>
                  <Input
                    value={settings?.sla_tag_name || 'SLA Breach'}
                    onChange={(e) => updateSetting('sla_tag_name', e.target.value)}
                    disabled={!canEdit}
                    data-testid="sla-tag"
                  />
                </div>
                
                <div>
                  <Label>Escalation Task Type</Label>
                  <p className="text-xs text-slate-500 mb-2">
                    Task type for escalation reviews
                  </p>
                  <Input
                    value={settings?.sla_task_type || 'Manager Review – SLA Breach'}
                    onChange={(e) => updateSetting('sla_task_type', e.target.value)}
                    disabled={!canEdit}
                    data-testid="sla-task-type"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sales Week Tab */}
        <TabsContent value="salesweek" className="space-y-4">
          {/* Sales Week Status Card */}
          {salesWeekStatus && (
            <Card className={`border ${
              salesWeekStatus.status === 'active' ? 'border-emerald-300 bg-emerald-50/50' :
              salesWeekStatus.status === 'active_fallback' ? 'border-amber-300 bg-amber-50/50' :
              salesWeekStatus.status === 'cancelled' ? 'border-red-300 bg-red-50/50' :
              salesWeekStatus.status === 'disabled' ? 'border-slate-300 bg-slate-50/50' :
              'border-slate-200 bg-white'
            }`}>
              <CardContent className="py-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${
                      salesWeekStatus.status === 'active' ? 'bg-emerald-500 animate-pulse' :
                      salesWeekStatus.status === 'active_fallback' ? 'bg-amber-500 animate-pulse' :
                      salesWeekStatus.status === 'cancelled' ? 'bg-red-500' :
                      'bg-slate-400'
                    }`} />
                    <div>
                      <p className="font-semibold text-slate-900">
                        Sales Week Status: {' '}
                        <span className={
                          salesWeekStatus.status === 'active' ? 'text-emerald-700' :
                          salesWeekStatus.status === 'active_fallback' ? 'text-amber-700' :
                          salesWeekStatus.status === 'cancelled' ? 'text-red-700' :
                          salesWeekStatus.status === 'disabled' ? 'text-slate-500' :
                          'text-slate-600'
                        }>
                          {salesWeekStatus.status === 'active' ? 'Active' :
                           salesWeekStatus.status === 'active_fallback' ? 'Active (Month-End Fallback)' :
                           salesWeekStatus.status === 'cancelled' ? 'Cancelled' :
                           salesWeekStatus.status === 'disabled' ? 'Disabled' :
                           'Inactive'}
                        </span>
                      </p>
                      {salesWeekStatus.controlling_event && (
                        <p className="text-sm text-slate-600">
                          Controlling Event: <span className="font-medium">{salesWeekStatus.controlling_event.name}</span>
                          <span className="text-slate-400 ml-2">
                            ({new Date(salesWeekStatus.controlling_event.start_datetime).toLocaleDateString()} - {new Date(salesWeekStatus.controlling_event.end_datetime).toLocaleDateString()})
                          </span>
                        </p>
                      )}
                      {salesWeekStatus.cancelled_event && (
                        <p className="text-sm text-red-600">
                          Cancelled: {salesWeekStatus.cancelled_event.name}
                        </p>
                      )}
                      {salesWeekStatus.fallback_info && (
                        <p className="text-sm text-amber-700">
                          Month-end in {salesWeekStatus.fallback_info.days_remaining} days ({salesWeekStatus.fallback_info.month_end_date})
                        </p>
                      )}
                      {salesWeekStatus.inactive_reason && (
                        <p className="text-sm text-slate-500">{salesWeekStatus.inactive_reason}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-4 text-sm">
                    <div className="text-center">
                      <p className="font-bold text-lg text-slate-900">{salesWeekStatus.open_tasks_count || 0}</p>
                      <p className="text-slate-500">Open Tasks</p>
                    </div>
                    <div className="text-center">
                      <p className="font-bold text-lg text-slate-900">{salesWeekStatus.leads_in_sales_week_stages || 0}</p>
                      <p className="text-slate-500">Leads in Scope</p>
                    </div>
                  </div>
                </div>
                {salesWeekStatus.status === 'cancelled' && salesWeekStatus.open_tasks_count > 0 && (
                  <div className="mt-3 p-2 bg-red-100 rounded-lg text-sm text-red-700 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Sales Week cancelled — {salesWeekStatus.open_tasks_count} existing tasks remain open. Admin/SM can bulk-close them manually if desired.
                  </div>
                )}
                {salesWeekStatus.warning && (
                  <div className="mt-3 p-2 bg-amber-100 rounded-lg text-sm text-amber-700 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {salesWeekStatus.warning}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-gold" />
                Sales Week Settings
              </CardTitle>
              <CardDescription>
                Event-driven sales push automation based on active Sales Week events
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Enable Sales Week</Label>
                  <p className="text-sm text-slate-500">Auto-create closing tasks during sales week</p>
                </div>
                <Switch
                  checked={settings?.sales_week_enabled || false}
                  onCheckedChange={(v) => updateSetting('sales_week_enabled', v)}
                  disabled={!canEdit}
                  data-testid="sales-week-toggle"
                />
              </div>

              {/* Mode Selection */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                <Label className="font-semibold">Trigger Mode</Label>
                <div className="grid gap-3 md:grid-cols-2">
                  <div
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      settings?.sales_week_mode === 'event_driven' || !settings?.sales_week_mode
                        ? 'border-gold bg-gold/10' 
                        : 'border-slate-200 hover:border-slate-300'
                    } ${!canEdit ? 'cursor-not-allowed opacity-60' : ''}`}
                    onClick={() => canEdit && updateSetting('sales_week_mode', 'event_driven')}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="w-4 h-4 text-gold" />
                      <span className="font-medium">Event-Driven</span>
                      <Badge className="bg-emerald-100 text-emerald-800 text-xs">Recommended</Badge>
                    </div>
                    <p className="text-sm text-slate-600">
                      Only run when an active Sales Week event date range includes today. 
                      Create events in Events page with category "Sales Week".
                    </p>
                  </div>
                  <div
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      settings?.sales_week_mode === 'month_end_fallback'
                        ? 'border-gold bg-gold/10' 
                        : 'border-slate-200 hover:border-slate-300'
                    } ${!canEdit ? 'cursor-not-allowed opacity-60' : ''}`}
                    onClick={() => canEdit && updateSetting('sales_week_mode', 'month_end_fallback')}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4 text-amber-600" />
                      <span className="font-medium">Month-End Fallback</span>
                    </div>
                    <p className="text-sm text-slate-600">
                      If no active event exists, fallback to last X days of month logic.
                      Good for teams not yet using Events module.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className={settings?.sales_week_mode !== 'month_end_fallback' ? 'opacity-50' : ''}>
                  <Label>Days Before Month End <span className="text-xs text-slate-400">(fallback only)</span></Label>
                  <Input
                    type="number"
                    min="1"
                    max="14"
                    value={settings?.sales_week_days || 7}
                    onChange={(e) => updateSetting('sales_week_days', parseInt(e.target.value) || 7)}
                    disabled={!canEdit || settings?.sales_week_mode !== 'month_end_fallback'}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Task Frequency</Label>
                  <Select
                    value={settings?.sales_week_frequency || 'daily'}
                    onValueChange={(v) => updateSetting('sales_week_frequency', v)}
                    disabled={!canEdit}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="every_2_days">Every 2 Days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select
                    value={settings?.sales_week_priority || 'high'}
                    onValueChange={(v) => updateSetting('sales_week_priority', v)}
                    disabled={!canEdit}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map(p => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Task Type</Label>
                  <Input
                    value={settings?.sales_week_task_type || 'Sales Week Closing Call'}
                    onChange={(e) => updateSetting('sales_week_task_type', e.target.value)}
                    disabled={!canEdit}
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label className="mb-2 block">Included Stages</Label>
                <div className="flex flex-wrap gap-2">
                  {salesWeekStages.map(stage => {
                    const isSelected = settings?.sales_week_stages?.includes(stage.value);
                    return (
                      <Badge
                        key={stage.value}
                        variant={isSelected ? 'default' : 'outline'}
                        className={`cursor-pointer transition-colors ${
                          isSelected ? 'bg-gold text-slate-900 hover:bg-gold/80' : 'hover:bg-slate-100'
                        } ${!canEdit ? 'cursor-not-allowed opacity-60' : ''}`}
                        onClick={() => canEdit && toggleSalesWeekStage(stage.value)}
                      >
                        {stage.label}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Template Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Task Template</DialogTitle>
          </DialogHeader>
          {editTemplate && (
            <div className="space-y-4">
              <div>
                <Label>Template Name</Label>
                <Input
                  value={editTemplate.name}
                  onChange={(e) => updateEditTemplate('name', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Task Title</Label>
                <Input
                  value={editTemplate.default_title}
                  onChange={(e) => updateEditTemplate('default_title', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Priority</Label>
                  <Select
                    value={editTemplate.default_priority}
                    onValueChange={(v) => updateEditTemplate('default_priority', v)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map(p => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Assign To</Label>
                  <Select
                    value={editTemplate.assignee_rule}
                    onValueChange={(v) => updateEditTemplate('assignee_rule', v)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ASSIGNEE_RULES.map(r => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Due Rule</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <Input
                    type="number"
                    placeholder="Days"
                    value={editTemplate.due_rule?.days || 0}
                    onChange={(e) => updateDueRule('days', parseInt(e.target.value) || 0)}
                  />
                  <Input
                    type="number"
                    placeholder="Hours"
                    value={editTemplate.due_rule?.hours || 0}
                    onChange={(e) => updateDueRule('hours', parseInt(e.target.value) || 0)}
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">Days and hours from stage entry</p>
              </div>
              <div>
                <Label>Duplicate Prevention</Label>
                <Select
                  value={editTemplate.duplicate_prevention_rule}
                  onValueChange={(v) => updateEditTemplate('duplicate_prevention_rule', v)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DUPLICATE_RULES.map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSaveTemplate}
                  disabled={saving}
                  className="bg-gold hover:bg-gold/90 text-slate-900"
                >
                  {saving ? 'Saving...' : 'Save Template'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
